import { isDefined } from "@clipboard-health/util-ts";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useRef } from "react";
import { useLocation } from "react-router-dom";

import { SHIFT_DISCOVERY_WORKPLACE_OPEN_SHIFTS_MODAL_PATH } from "../../ShiftDiscovery/paths";
import { useWorkplaceModalsDataContext } from "../../ShiftDiscovery/useWorkplaceModalsDataContext";
import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { WorkplaceModalContent } from "../Modal/Content";
import { useWorkplaceOpenShiftsData } from "../OpenShifts/useWorkplaceOpenShiftsData";
import { useWorkplaceStaffJobsData } from "../OpenShifts/useWorkplaceStaffJobsData";
import type { WorkplaceLocationState, WorkplaceProfile } from "../types";
import { isHomeHealthOrHospiceWorkplace } from "../utils";
import { WorkplaceProfileModalContent } from "./ModalContent";
import { WorkplaceProfileModalContentSkeleton } from "./ModalContentSkeleton";
import { WorkplaceProfileModalFooter } from "./ModalFooter";

export interface WorkplaceProfileModalProps {
  modalState: UseModalState;
  workplaceId: string;
  workplace?: WorkplaceProfile;
  isLoading?: boolean;
}

export function WorkplaceProfileModal(props: WorkplaceProfileModalProps) {
  const { modalState, workplaceId, workplace, isLoading } = props;

  const { generateModalPath } = useWorkplaceModalsDataContext();

  const {
    data: { unfilteredWorkplaceShifts },
    isLoading: openShiftsIsLoading,
  } = useWorkplaceOpenShiftsData(workplace);

  const {
    isJobsEnabled,
    isLoading: staffJobsIsLoading,
    placementsCount,
    placementCandidateId,
  } = useWorkplaceStaffJobsData(workplaceId);

  const hasOpenShifts = unfilteredWorkplaceShifts.length > 0;
  const hasStaffJobs = placementsCount > 0;
  const isFooterLoading = openShiftsIsLoading || staffJobsIsLoading;

  const scrollRef = useRef<HTMLDivElement>(null);

  useScrollToTopOnStatusTap(scrollRef);

  const location = useLocation<WorkplaceLocationState>();
  const isFromOpenShiftsModal = location.state?.origin === "WORKPLACE_OPEN_SHIFTS_MODAL";

  const shouldShowOpenShiftsButton =
    !isFromOpenShiftsModal &&
    (isFooterLoading || !(isHomeHealthOrHospiceWorkplace(workplace?.type) && !hasOpenShifts));

  const hasAnyFooterButton = shouldShowOpenShiftsButton || (isJobsEnabled && hasStaffJobs);

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="primary">
      <StickyScrollPageHeader
        title={workplace?.name ?? ""}
        isLoading={isLoading}
        scrollContainerRef={scrollRef}
      />

      <WorkplaceModalContent
        ref={scrollRef}
        sx={hasAnyFooterButton ? undefined : { paddingBottom: 7 }}
      >
        {isDefined(workplace) && <WorkplaceProfileModalContent workplaceProfile={workplace} />}

        {isLoading && !isDefined(workplace) && <WorkplaceProfileModalContentSkeleton />}
      </WorkplaceModalContent>

      <WorkplaceProfileModalFooter
        workplaceId={workplaceId}
        isLoading={isFooterLoading}
        source="flex-workplace-profile-modal"
        locationState={{ origin: "WORKPLACE_PROFILE_MODAL" as const }}
        openShifts={
          shouldShowOpenShiftsButton
            ? {
                count: unfilteredWorkplaceShifts.length,
                href: generateModalPath(SHIFT_DISCOVERY_WORKPLACE_OPEN_SHIFTS_MODAL_PATH, {
                  workplaceId,
                }),
                analyticsTrigger: "Workplace Profile Modal",
              }
            : undefined
        }
        staffJobs={
          isJobsEnabled && hasStaffJobs ? { placementsCount, placementCandidateId } : undefined
        }
      />
    </FullScreenDialog>
  );
}
